import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, X } from 'lucide-react';
import { SEO } from '../components/SEO';

const API = `${window.location.origin}/api`;

const CATEGORIES = ['All', 'Wedding', 'Corporate', 'Gala', 'Private', 'Social'];

export const Portfolio = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('All');
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/portfolio`)
      .then((res) => setItems(res.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (category === 'All') return items;
    return items.filter((i) => i.category === category);
  }, [items, category]);

  return (
    <div className="min-h-screen pt-24 bg-[#050505]" data-testid="portfolio-page">
      <SEO
        title="Event Portfolio | White Glove Event Concierge"
        description="A curated record of strategic event advisory across weddings, corporate events, galas, and private celebrations in Nashville."
      />
      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="text-[#D4AF37] text-sm uppercase tracking-[0.2em]">Portfolio</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mt-4 mb-6">
              A Record of <em className="text-gold-gradient">Quiet Excellence</em>
            </h1>
            <p className="text-white/60 text-lg max-w-2xl mx-auto leading-relaxed">
              Selected moments from events we have advised on — illustrating the precision,
              restraint, and hospitality that define our work.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter bar */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-center justify-center gap-3 border-y border-white/5 py-6">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`text-xs uppercase tracking-[0.25em] px-5 py-2.5 transition-all ${
                  category === cat
                    ? 'bg-[#D4AF37] text-black'
                    : 'text-white/50 hover:text-white border border-white/10 hover:border-white/30'
                }`}
                data-testid={`portfolio-filter-${cat.toLowerCase()}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="py-24 text-center">
              <div className="w-10 h-10 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center text-white/40 text-sm" data-testid="portfolio-empty">
              {items.length === 0
                ? 'Our portfolio is being curated. Check back soon.'
                : `No ${category.toLowerCase()} events to display just yet.`}
            </div>
          ) : (
            <motion.div
              layout
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              data-testid="portfolio-grid"
            >
              <AnimatePresence>
                {filtered.map((item, idx) => (
                  <motion.button
                    key={item.item_id}
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.45, delay: idx * 0.04, ease: [0.22, 1, 0.36, 1] }}
                    onClick={() => setActive(item)}
                    className="group relative overflow-hidden bg-[#0a0a0a] border border-white/5 hover:border-[#D4AF37]/40 transition-colors text-left"
                    data-testid={`portfolio-item-${item.item_id}`}
                  >
                    <div className="aspect-[4/5] overflow-hidden relative">
                      {item.media_type === 'video' ? (
                        <>
                          {item.poster_url ? (
                            <img
                              src={item.poster_url}
                              alt={item.title}
                              loading="lazy"
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                          ) : (
                            <video
                              src={item.image_url}
                              muted
                              playsInline
                              preload="metadata"
                              className="w-full h-full object-cover"
                            />
                          )}
                          <span className="absolute top-3 right-3 z-10 flex items-center gap-1 text-[10px] uppercase tracking-widest text-black bg-[#D4AF37] px-2 py-1">
                            ▶ Video
                          </span>
                        </>
                      ) : (
                        <img
                          src={item.image_url}
                          alt={item.title}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-80 group-hover:opacity-100 transition-opacity pointer-events-none" />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <p className="text-[#D4AF37] text-[10px] uppercase tracking-[0.25em] mb-2">
                        {item.category}
                      </p>
                      <h3 className="font-heading text-white text-xl md:text-2xl leading-tight">
                        {item.title}
                      </h3>
                      {item.location && (
                        <p className="text-white/60 text-xs mt-2">{item.location}</p>
                      )}
                    </div>
                  </motion.button>
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-6"
            onClick={() => setActive(null)}
            data-testid="portfolio-lightbox"
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-5xl w-full bg-[#0a0a0a] border border-white/10 grid md:grid-cols-2"
            >
              <button
                onClick={() => setActive(null)}
                className="absolute top-4 right-4 p-2 bg-black/60 text-white/60 hover:text-white border border-white/10 z-10"
                data-testid="portfolio-lightbox-close"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="aspect-square md:aspect-auto bg-black flex items-center justify-center">
                {active.media_type === 'video' ? (
                  <video
                    src={active.image_url}
                    poster={active.poster_url || undefined}
                    controls
                    autoPlay
                    playsInline
                    className="w-full h-full object-contain bg-black"
                    data-testid="portfolio-lightbox-video"
                  />
                ) : (
                  <img src={active.image_url} alt={active.title} className="w-full h-full object-cover" />
                )}
              </div>
              <div className="p-8 md:p-10 flex flex-col justify-center">
                <p className="text-[#D4AF37] text-xs uppercase tracking-[0.25em] mb-3">
                  {active.category}
                </p>
                <h2 className="font-heading text-3xl text-white mb-4">{active.title}</h2>
                {active.location && (
                  <p className="text-white/60 text-sm mb-1">{active.location}</p>
                )}
                {active.event_date && (
                  <p className="text-white/40 text-xs uppercase tracking-widest mb-6">{active.event_date}</p>
                )}
                {active.description && (
                  <p className="text-white/75 text-sm leading-relaxed mb-8">{active.description}</p>
                )}
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-[0.25em] hover:gap-3 transition-all"
                >
                  Plan something similar <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Portfolio;
